import * as React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { cva, type VariantProps } from 'class-variance-authority';
import { ChevronDown, ChevronUp, PanelLeft } from 'lucide-react';

import {
  ShadTooltip,
  ShadTooltipContent,
  ShadTooltipProvider,
  ShadTooltipTrigger,
} from 'components/atoms/ShadTooltip';
import { useIsMobile } from 'hooks/useIsMobile';
import { cn } from 'utils/cn';

type RenderProp = { className?: string; children?: React.ReactNode };

function cloneWithProps(
  element: React.ReactElement,
  props: Record<string, unknown>,
) {
  const existing = element.props as RenderProp;
  const { className, children, ...rest } = props;
  return React.cloneElement(element, {
    ...rest,
    className: cn(className as string, existing.className),
    children: children ? (
      <>
        {existing.children}
        {children}
      </>
    ) : (
      existing.children
    ),
  } as Record<string, unknown>);
}

const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = '240px';
const SIDEBAR_WIDTH_ICON = '64px';
const SIDEBAR_KEYBOARD_SHORTCUT = 'b';

export type ShadSidebarContextValue = {
  state: 'expanded' | 'collapsed';
  open: boolean;
  setOpen: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
};

const ShadSidebarContext = createContext<ShadSidebarContextValue | null>(null);

export function useShadSidebar(): ShadSidebarContextValue {
  const context = useContext(ShadSidebarContext);
  if (!context) {
    throw new Error(
      'useShadSidebar must be used within a ShadSidebarProvider.',
    );
  }
  return context;
}

export const ShadSidebarProvider = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    defaultOpen?: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    expandedWidth?: string;
    collapsedWidth?: string;
    persistKey?: string;
    keyboardShortcut?: string | null;
  }
>(
  (
    {
      defaultOpen = true,
      open: openProp,
      onOpenChange: setOpenProp,
      expandedWidth = SIDEBAR_WIDTH,
      collapsedWidth = SIDEBAR_WIDTH_ICON,
      persistKey,
      keyboardShortcut = SIDEBAR_KEYBOARD_SHORTCUT,
      className,
      style,
      children,
      ...props
    },
    ref,
  ) => {
    const isMobile = useIsMobile();

    const [_open, _setOpen] = useState(() => {
      if (persistKey && typeof document !== 'undefined') {
        const cookie = document.cookie
          .split('; ')
          .find(row => row.startsWith(`${persistKey}=`));
        if (cookie) {
          return cookie.split('=')[1] === 'true';
        }
      }
      return defaultOpen;
    });
    const open = openProp ?? _open;
    const setOpen = useCallback(
      (value: boolean | ((value: boolean) => boolean)) => {
        const openState = typeof value === 'function' ? value(open) : value;
        if (setOpenProp) {
          setOpenProp(openState);
        } else {
          _setOpen(openState);
        }
        if (persistKey && typeof document !== 'undefined') {
          document.cookie = `${persistKey}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
        }
      },
      [setOpenProp, open, persistKey],
    );

    const toggleSidebar = useCallback(() => {
      setOpen(prev => !prev);
    }, [setOpen]);

    useEffect(() => {
      if (keyboardShortcut === null) return;

      const handleKeyDown = (event: KeyboardEvent) => {
        if (
          event.key === keyboardShortcut &&
          (event.metaKey || event.ctrlKey)
        ) {
          event.preventDefault();
          toggleSidebar();
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [toggleSidebar, keyboardShortcut]);

    const state = open ? 'expanded' : 'collapsed';

    const contextValue = useMemo<ShadSidebarContextValue>(
      () => ({
        state,
        open,
        setOpen,
        isMobile,
        toggleSidebar,
      }),
      [state, open, setOpen, isMobile, toggleSidebar],
    );

    return (
      <ShadSidebarContext.Provider value={contextValue}>
        <ShadTooltipProvider delay={0}>
          <div
            ref={ref}
            className={cn(
              'group/sidebar-wrapper flex min-h-svh w-full overflow-x-clip',
              className,
            )}
            style={
              {
                '--sidebar-width': expandedWidth,
                '--sidebar-width-icon': collapsedWidth,
                ...style,
              } as React.CSSProperties
            }
            {...props}
          >
            {children}
          </div>
        </ShadTooltipProvider>
      </ShadSidebarContext.Provider>
    );
  },
);
ShadSidebarProvider.displayName = 'ShadSidebarProvider';

export const ShadSidebar = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    side?: 'left' | 'right';
    variant?: 'sidebar' | 'floating' | 'inset';
    collapsible?: 'offcanvas' | 'icon' | 'none';
  }
>(
  (
    {
      side = 'left',
      variant = 'sidebar',
      collapsible = 'offcanvas',
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const { state } = useShadSidebar();

    if (collapsible === 'none') {
      return (
        <div
          ref={ref}
          className={cn(
            'flex h-full w-[--sidebar-width] flex-col bg-background text-secondary',
            className,
          )}
          {...props}
        >
          {children}
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className="group peer text-secondary"
        data-collapsible={state === 'collapsed' ? collapsible : ''}
        data-side={side}
        data-state={state}
        data-variant={variant}
      >
        {/* Spacer — reserves layout width so content shifts with the sidebar */}
        <div
          className={cn(
            'relative h-svh w-[--sidebar-width] bg-transparent transition-[width] duration-200 ease-linear',
            'group-data-[collapsible=offcanvas]:w-0',
            'group-data-[side=right]:rotate-180',
            variant === 'floating' || variant === 'inset'
              ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.sm1))]'
              : 'group-data-[collapsible=icon]:w-[--sidebar-width-icon]',
          )}
        />
        <div
          className={cn(
            'fixed inset-y-0 z-navbar flex h-svh w-[--sidebar-width]',
            'transition-[left,right,width] duration-200 ease-linear',
            side === 'left'
              ? 'left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]'
              : 'right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]',
            variant === 'floating' || variant === 'inset'
              ? 'p-xxs2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.sm1)_+2px)]'
              : 'group-data-[collapsible=icon]:w-[--sidebar-width-icon] group-data-[side=left]:border-r group-data-[side=right]:border-l border-border-subtle',
            className,
          )}
          {...props}
        >
          <div
            className={cn(
              'flex h-full w-full flex-col bg-background',
              'group-data-[variant=floating]:rounded-medium group-data-[variant=floating]:border group-data-[variant=floating]:border-border-subtle group-data-[variant=floating]:shadow-overlay',
            )}
            data-sidebar="sidebar"
          >
            {children}
          </div>
        </div>
      </div>
    );
  },
);
ShadSidebar.displayName = 'ShadSidebar';

const isMac =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad/.test(navigator.userAgent);

export const ShadSidebarTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'>
>(({ className, onClick, ...props }, ref) => {
  const { toggleSidebar } = useShadSidebar();

  return (
    <ShadTooltip>
      <ShadTooltipTrigger
        render={
          <button
            ref={ref}
            className={cn(
              'inline-flex h-[28px] w-[28px] items-center justify-center',
              'rounded-xSmall text-secondary',
              'hover:bg-background-accent hover:text-primary',
              className,
            )}
            data-sidebar="trigger"
            onClick={event => {
              onClick?.(event);
              toggleSidebar();
            }}
            {...props}
          />
        }
      >
        <PanelLeft className="h-[16px] w-[16px]" />
        <span className="sr-only">Toggle Sidebar</span>
      </ShadTooltipTrigger>
      <ShadTooltipContent side="right" className="flex items-center gap-xs2">
        Toggle Sidebar
        <kbd className="pointer-events-none ml-auto inline-flex h-[20px] items-center gap-xxs1 rounded-xSmall border border-border-subtle bg-background-selected px-xxs2 font-mono text-[10px] font-medium text-secondary">
          {isMac ? '⌘B' : 'Ctrl+B'}
        </kbd>
      </ShadTooltipContent>
    </ShadTooltip>
  );
});
ShadSidebarTrigger.displayName = 'ShadSidebarTrigger';

export const ShadSidebarHeader = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col gap-xxs2 p-xs2', className)}
    data-sidebar="header"
    {...props}
  />
));
ShadSidebarHeader.displayName = 'ShadSidebarHeader';

export const ShadSidebarFooter = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col gap-xxs2 p-xs2', className)}
    data-sidebar="footer"
    {...props}
  />
));
ShadSidebarFooter.displayName = 'ShadSidebarFooter';

export const ShadSidebarSeparator = React.forwardRef<
  HTMLHRElement,
  React.ComponentProps<'hr'>
>(({ className, ...props }, ref) => (
  <hr
    ref={ref}
    className={cn(
      'mx-xs2 shrink-0 border-none bg-border-subtle h-xxs1 w-auto',
      className,
    )}
    data-sidebar="separator"
    {...props}
  />
));
ShadSidebarSeparator.displayName = 'ShadSidebarSeparator';

const SCROLL_INDICATOR_MAX = 36;
const SCROLL_THRESHOLD = 1;

function useScrollOverflow(scrollRef: React.RefObject<HTMLDivElement | null>) {
  const [scrollTop, setScrollTop] = useState(0);
  const [scrollBottom, setScrollBottom] = useState(0);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let rafId: number | null = null;

    const update = () => {
      const top = el.scrollTop;
      const bottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      setScrollTop(
        top > SCROLL_THRESHOLD ? Math.min(SCROLL_INDICATOR_MAX, top) : 0,
      );
      setScrollBottom(
        bottom > SCROLL_THRESHOLD ? Math.min(SCROLL_INDICATOR_MAX, bottom) : 0,
      );
    };

    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        update();
        rafId = null;
      });
    };

    update();

    const observer = new MutationObserver(handleScroll);
    observer.observe(el, { childList: true, subtree: true });
    el.addEventListener('scroll', handleScroll);

    return () => {
      el.removeEventListener('scroll', handleScroll);
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [scrollRef]);

  return { scrollTop, scrollBottom };
}

function ShadSidebarScrollIndicator({
  position,
  visible,
  scrollRef,
}: {
  position: 'top' | 'bottom';
  visible: boolean;
  scrollRef: React.RefObject<HTMLDivElement | null>;
}) {
  const isTop = position === 'top';
  const Icon = isTop ? ChevronUp : ChevronDown;

  const handleClick = () => {
    scrollRef.current?.scrollTo({
      top: isTop ? 0 : scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn(
        'absolute left-0 right-0 z-10 flex cursor-pointer items-center justify-center transition-opacity duration-200',
        isTop ? 'top-0' : 'bottom-0',
        visible ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
      style={{ height: SCROLL_INDICATOR_MAX }}
    >
      <div
        className={cn(
          'absolute inset-0',
          isTop
            ? 'bg-gradient-to-b from-background from-50% to-transparent'
            : 'bg-gradient-to-t from-background from-50% to-transparent',
        )}
      />
      <Icon className={cn('relative size-[16px] text-secondary')} />
    </div>
  );
}

export const ShadSidebarContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { scrollTop, scrollBottom } = useScrollOverflow(scrollRef);

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      scrollRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [ref],
  );

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={setRefs}
        className={cn(
          'flex min-h-0 flex-1 flex-col gap-xxs2 overflow-auto',
          '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          'group-data-[collapsible=icon]:overflow-x-hidden group-data-[collapsible=icon]:overflow-y-auto',
          className,
        )}
        data-sidebar="content"
        {...props}
      />
      <ShadSidebarScrollIndicator
        position="top"
        visible={scrollTop > 0}
        scrollRef={scrollRef}
      />
      <ShadSidebarScrollIndicator
        position="bottom"
        visible={scrollBottom > 0}
        scrollRef={scrollRef}
      />
    </div>
  );
});
ShadSidebarContent.displayName = 'ShadSidebarContent';

export const ShadSidebarGroup = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('relative flex w-full min-w-0 flex-col', className)}
    data-sidebar="group"
    {...props}
  />
));
ShadSidebarGroup.displayName = 'ShadSidebarGroup';

export const ShadSidebarGroupLabel = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & { render?: React.ReactElement }
>(({ className, render, ...props }, ref) => {
  const mergedClassName = cn(
    'flex h-[32px] shrink-0 items-center rounded-small px-xs2',
    'text-header-small font-medium text-secondary/70',
    'outline-none ring-border-focus focus-visible:ring-2',
    'transition-[margin,opacity] duration-200 ease-linear',
    'group-data-[collapsible=icon]:opacity-0',
    className,
  );

  if (render) {
    return cloneWithProps(render, {
      ref,
      className: mergedClassName,
      'data-sidebar': 'group-label',
      ...props,
    });
  }

  return (
    <div
      ref={ref}
      className={mergedClassName}
      data-sidebar="group-label"
      {...props}
    />
  );
});
ShadSidebarGroupLabel.displayName = 'ShadSidebarGroupLabel';

export const ShadSidebarMenu = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<'ul'>
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn('flex w-full min-w-0 flex-col', className)}
    data-sidebar="menu"
    {...props}
  />
));
ShadSidebarMenu.displayName = 'ShadSidebarMenu';

export const ShadSidebarMenuItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<'li'>
>(({ className, ...props }, ref) => (
  <li
    ref={ref}
    className={cn('group/menu-item relative', className)}
    data-sidebar="menu-item"
    {...props}
  />
));
ShadSidebarMenuItem.displayName = 'ShadSidebarMenuItem';

const shadSidebarMenuButtonVariants = cva(
  [
    'peer/menu-button flex w-full items-center gap-sm1 overflow-hidden',
    'rounded-small px-xs3 py-xs1 text-left text-body-regular',
    'outline-none ring-border-focus transition-[width,height,padding]',
    'hover:bg-background-accent hover:text-primary',
    'focus-visible:ring-2',
    'active:bg-background-selected active:text-primary',
    'disabled:pointer-events-none disabled:opacity-50',
    'aria-disabled:pointer-events-none aria-disabled:opacity-50',
    'data-[active=true]:bg-background-selected data-[active=true]:text-primary',
    '[&>span:last-child]:truncate [&_svg]:size-[20px] [&_svg]:shrink-0',
    // Collapsed icon mode
    'group-data-[collapsible=icon]:size-[28px]',
    'group-data-[collapsible=icon]:rounded-xSmall',
    'group-data-[collapsible=icon]:justify-center',
    'group-data-[collapsible=icon]:p-0',
    'group-data-[collapsible=icon]:gap-0',
    'group-data-[collapsible=icon]:mx-auto',
    'group-data-[collapsible=icon]:[&>*:not(:first-child)]:hidden',
  ].join(' '),
  {
    variants: {
      variant: {
        default: '',
        outline:
          'bg-background shadow-[0_0_0_1px_var(--tw-border-subtle)] hover:bg-background-accent hover:text-primary hover:shadow-[0_0_0_1px_var(--tw-background-selected)]',
      },
      size: {
        small: 'h-[28px]',
        medium: 'h-[32px]',
        large: 'h-[48px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'medium',
    },
  },
);

export { shadSidebarMenuButtonVariants };

export const ShadSidebarMenuButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> & {
    render?: React.ReactElement;
    isActive?: boolean;
    tooltip?: string | React.ComponentProps<typeof ShadTooltipContent>;
  } & VariantProps<typeof shadSidebarMenuButtonVariants>
>(
  (
    {
      render,
      isActive = false,
      variant = 'default',
      size = 'medium',
      tooltip,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const { isMobile, state } = useShadSidebar();

    const mergedClassName = cn(
      shadSidebarMenuButtonVariants({ variant, size }),
      className,
    );

    const sharedProps = {
      'data-active': isActive,
      'data-sidebar': 'menu-button',
      'data-size': size,
      ...props,
    };

    const button = render ? (
      cloneWithProps(render, {
        ref,
        className: mergedClassName,
        children,
        ...sharedProps,
      })
    ) : (
      <button ref={ref} className={mergedClassName} {...sharedProps}>
        {children}
      </button>
    );

    if (!tooltip) {
      return button;
    }

    const tooltipProps =
      typeof tooltip === 'string' ? { children: tooltip } : tooltip;

    // key={state} forces remount on collapse/expand, resetting Base UI hover state
    // so the tooltip can't linger after sidebar collapses while item was hovered
    return (
      <ShadTooltip key={state}>
        <ShadTooltipTrigger render={button} />
        <ShadTooltipContent
          align="center"
          hidden={state !== 'collapsed' || isMobile}
          side="right"
          {...tooltipProps}
        />
      </ShadTooltip>
    );
  },
);
ShadSidebarMenuButton.displayName = 'ShadSidebarMenuButton';
